import { useState } from "react";
import { SwapHeader } from "./swap-header";
import { useHubStore } from "../use-hub-store";
import { cn } from "@/lib/utils";
import {
  SWAP_PROMPTS,
  type SwapPrompt,
} from "@somethings-ui/core/src/swap-prompts";
import { usePostHogEvent } from "@/lib/posthog";

interface PromptItemProps {
  prompt: SwapPrompt;
  isSelected: boolean;
  onSelect: () => void;
}

function PromptItem({ prompt, isSelected, onSelect }: PromptItemProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 rounded-2xl border bg-white px-4 py-6 text-left transition-colors",
        isSelected
          ? "border-gray-900"
          : "border-gray-200 hover:border-gray-300",
      )}
    >
      <div className="flex-1">
        <p className="text-base font-medium text-gray-900">{prompt.title}</p>
        <p className="text-sm text-gray-500">{prompt.subtitle}</p>
      </div>
      <div
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
          isSelected
            ? "border-gray-900 bg-gray-900"
            : "border-gray-300 bg-white",
        )}
      >
        {isSelected && <div className="h-2 w-2 rounded-full bg-white" />}
      </div>
    </button>
  );
}

export function SelectSwapPromptView() {
  const navigateToView = useHubStore((state) => state.navigateToView);
  const setDraftSwap = useHubStore((state) => state.setDraftSwap);
  const draftSwap = useHubStore((state) => state.draftSwap);
  const { dispatchEvent } = usePostHogEvent("mentor_hub", { onMount: false });

  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(
    draftSwap?.promptId ?? null,
  );

  const selectedPrompt = SWAP_PROMPTS.find(
    (p: SwapPrompt) => p.id === selectedPromptId,
  );

  const handleContinue = () => {
    if (!selectedPrompt) return;

    dispatchEvent("mentor_hub_swap_prompt_selected", {
      promptId: selectedPrompt.id,
    });
    setDraftSwap({
      promptId: selectedPrompt.id,
      photo: draftSwap?.photo ?? null,
    });
    navigateToView("upload-swap");
  };

  return (
    <div className="flex h-full flex-col bg-white">
      <SwapHeader
        title="Swaps"
        subtitle="Create a swap for you and your teen"
        showBackButton={false}
        showInfoButton
      />

      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div className="flex flex-col gap-4">
          {SWAP_PROMPTS.map((prompt: SwapPrompt) => (
            <PromptItem
              key={prompt.id}
              prompt={prompt}
              isSelected={selectedPromptId === prompt.id}
              onSelect={() => setSelectedPromptId(prompt.id)}
            />
          ))}
        </div>
      </div>

      {selectedPromptId && (
        <div className="border-t border-gray-200 bg-white p-4">
          <button
            type="button"
            onClick={handleContinue}
            className="w-full rounded-full bg-black py-4 text-base font-semibold text-white transition-colors hover:bg-gray-800"
          >
            Continue
          </button>
        </div>
      )}
    </div>
  );
}
